import { DatatableHead } from '@monksflow/monks-ui';
import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { TABLE_CLASSES } from '../Table.constants';
import type { TableSortCellProps } from './TableSortCell.types';

export function TableSortCell<TSortKey extends string>({
  className,
  sortKey,
  sortBy,
  sortDirection,
  onSort,
  children,
  adornment,
}: TableSortCellProps<TSortKey>) {
  const { t } = useTranslation('common');
  const isActive = sortBy === sortKey;

  // Neutral double-arrow on an idle column, directional arrow on the sorting one, so the
  // header row says both which column sorts and which way.
  const SortIcon = !isActive
    ? ArrowUpDownIcon
    : sortDirection === 'desc'
      ? ArrowDownIcon
      : ArrowUpIcon;

  return (
    <DatatableHead
      showBorder={false}
      aria-sort={isActive ? (sortDirection === 'desc' ? 'descending' : 'ascending') : undefined}
      className={twMerge(TABLE_CLASSES.headCell, className)}
    >
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="inline-flex cursor-pointer items-center gap-1 select-none"
          onClick={() => {
            onSort(sortKey);
          }}
        >
          {children}
          <SortIcon className="size-3.5" data-testid={`sort-icon-${sortKey}`} />
          {isActive && (
            <span className="sr-only">
              {sortDirection === 'desc'
                ? t('components.tableSortCell.sortedDescending')
                : t('components.tableSortCell.sortedAscending')}
            </span>
          )}
        </button>
        {adornment}
      </div>
    </DatatableHead>
  );
}
